import { Page } from 'components/Page/Page';
import { Filters } from 'components/Filters/Filters';
import { ListTable } from 'components/ListTable/ListTable';
import { InfiniteScroll } from 'components/ListTable/InfiniteScroll/InfiniteScroll';
import { TableHeader, TableItem } from 'components/ListTable/types';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'hooks/useDebounce';
import isEqual from 'lodash/isEqual';
import { useInvoiceVerificationFilters } from './hooks/useInvoiceVerificationFilters';
import { useInfiniteInvoiceVerification } from 'services/repositories/invoiceVerification/invoiceVerification';
import { InvoiceVerificationStatusBadge } from './components/InvoiceVerificationStatusBadge/InvoiceVerificationStatusBadge';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDay } from 'support/helpers/dateTime/dateTime';
import { formatCurrency } from 'support/helpers/currency/currency';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { InvoiceVerificationRoutes } from 'support/types';
import { createHandle } from 'services/breadcrumbs/breadcrumbs';
import { TFunction } from 'i18next';

export const InvoiceVerification = () => {
  const { t } = useTranslation();
  const { filters, filterValues, updateFilters, clearAllFilters, perPage } = useInvoiceVerificationFilters();

  const debouncedFilterValues = useDebounce(filterValues, 150);
  const differentFilters = !isEqual(debouncedFilterValues, filterValues);
  const hasFilters = debouncedFilterValues && debouncedFilterValues.length > 0;

  const {
    data: invoiceData,
    isLoading: invoiceLoading,
    isFetchingNextPage,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    isError,
  } = useInfiniteInvoiceVerification({
    variables: { query: { filterValues: debouncedFilterValues, perPage } },
  });

  const tableHeaders: Array<TableHeader> = [
    { label: t('invoiceVerification:listPage.tableHeaders.invoice') },
    { label: t('invoiceVerification:listPage.tableHeaders.related_orders') },
    { label: t('invoiceVerification:listPage.tableHeaders.responsible_user') },
    { label: t('invoiceVerification:listPage.tableHeaders.partner') },
    { label: t('invoiceVerification:listPage.tableHeaders.due_date') },
    { label: t('invoiceVerification:listPage.tableHeaders.due_amount'), alignRight: true },
    { label: t('invoiceVerification:listPage.tableHeaders.percentage_goods_received'), alignRight: true },
    { label: t('invoiceVerification:listPage.tableHeaders.status') },
  ];

  const data = invoiceData?.pages?.flatMap((page) => page?.data) ?? [];
  const emptyData = data.length === 0;

  const buildRow = (element: (typeof data)[0]) => {
    const cells: Array<TableItem> = [
      {
        element: element.message.external_message_ref || EMPTY_FIELD,
        key: `invoice-${element.id}`,
        options: { withHighlight: true },
      },
      {
        element: element.order_numbers.join(', '),
        key: 'related_orders',
        className: 'max-w-xs truncate',
        options: { withHighlight: true },
      },
      {
        element: element.responsible_user?.name || EMPTY_FIELD,
        key: 'responsible_user',
        className: 'max-w-xs truncate',
      },
      { element: element.sender.name, key: 'partner', className: 'max-w-xs truncate' },
      {
        element: <span className="tabular-nums">{element.due_date ? formatDay(element.due_date) : EMPTY_FIELD}</span>,
        key: 'due_date',
      },
      {
        element: (
          <span className="tabular-nums">
            {element.due_amount ? formatCurrency(element.due_amount, element.currency) : EMPTY_FIELD}
          </span>
        ),
        className: 'text-right',
        key: 'due_amount',
      },
      {
        element: (
          <span className="tabular-nums">
            {element.percentage_goods_received != undefined ? `${element.percentage_goods_received} %` : EMPTY_FIELD}
          </span>
        ),
        className: 'text-right',
        key: 'percentage_goods_received',
      },
      { element: <InvoiceVerificationStatusBadge status={element.status} />, key: 'status' },
    ];
    return cells;
  };

  return (
    <Page>
      <Page.Head
        title={t('invoiceVerification:listPage.title')}
        description={t('invoiceVerification:listPage.description')}
      >
        <Filters
          propertyFilters={filters}
          onFiltersChange={updateFilters}
          filterValues={filterValues}
          clearAllFilters={clearAllFilters}
        />
      </Page.Head>
      <Page.Section>
        <ListTable
          headers={tableHeaders}
          data={data}
          rowBuilder={buildRow}
          rowClickPath={(item) => routeToPage(InvoiceVerificationRoutes.detail, { invoiceId: String(item.id) })}
          isLoading={invoiceLoading || (differentFilters && emptyData)}
          pagination={
            <InfiniteScroll
              hasMore={Boolean(hasNextPage)}
              loadMore={fetchNextPage}
              loading={isFetchingNextPage}
              noMoreItemsEnabled={Boolean(hasPreviousPage)}
            />
          }
          emptyState={
            <ListTable.EmptyState
              cols={tableHeaders.length}
              cta={
                hasFilters
                  ? {
                      children: t('common:table.withFilters.emptyState.cta'),
                      onClick: clearAllFilters,
                      analyticsId: 'invoiceVerification:clear_filters',
                    }
                  : undefined
              }
            >
              {isError
                ? t('common:error.generic')
                : hasFilters
                  ? t('common:table.withFilters.emptyState.title')
                  : t('invoiceVerification:listPage.emptyTableState')}
            </ListTable.EmptyState>
          }
        />
      </Page.Section>
    </Page>
  );
};

InvoiceVerification.handle = createHandle(() => ({
  label: (t: TFunction) => t('invoiceVerification:listPage.breadcrumb'),
  to: routeToPage(InvoiceVerificationRoutes.root),
  key: InvoiceVerificationRoutes.root,
}));
